/*
 * Javascript module for passkey attestation and assertions
*/

rs.module("ui/framework/passkeys",

    [
        "controls/components/event-banner"
    ],

    function (eventBanner) {

        //
        // Initialize passkey login
        // 
        function initializeLogin(
            jsSelectors,
            backendUrls,
            returnUrl,
            errorCallback,
            passwordlessStartCallback,
            passwordlessCompleteCallback) {

            $.js(jsSelectors.loginButton).on('click', handleLoginClick);

            // abortcontroller for aborting the conditionalUI flow when the user clicks to manually start a passkey login
            // because you cannot have two navigator.crednetials.get calls at the same time
            var abortController = new AbortController();
            var abortSignal = abortController.signal;

            if (window.PublicKeyCredential && PublicKeyCredential.isConditionalMediationAvailable) {

                // Check if the browser supports the WebAuthn API
                PublicKeyCredential.isConditionalMediationAvailable().then(function (result) {
                    if (result) {
                        authenticate(true);
                    }
                });
            }

            // Handle the login (passkey) button click
            function handleLoginClick(event) {
                event.preventDefault();

                // abort the conditionalUI's triggering of credentials.get
                abortController.abort();

                // clear any existing passkey error
                errorCallback();

                authenticate(false);
            }

            //
            // Start the Passkey authentication process
            //
            function authenticate(conditional) {

                // If this isn't the Conditional UI flow, the user initiated the login, so we
                // want to notify the hosting page that the passkey login has started
                if (!conditional) {
                    passwordlessStartCallback();
                }

                var rememberMe = $.js(jsSelectors.rememberMe, { required: false }).is(':checked')

                // Create the assertion options server-side
                $.ajax({
                    type: "POST",
                    url: backendUrls.makeAssertionOptions,
                    data: { "rememberMe": rememberMe }
                }).done(function (response) {
                    var makeAssertionOptions = response;

                    if (makeAssertionOptions.status === "ok") {
                        const challenge = makeAssertionOptions.challenge.replace(/-/g, "+").replace(/_/g, "/");
                        makeAssertionOptions.challenge = Uint8Array.from(atob(challenge), c => c.charCodeAt(0));

                        var credentialArguments = {
                            publicKey: makeAssertionOptions,
                        };

                        if (conditional) {
                            credentialArguments.mediation = "conditional";
                            credentialArguments.signal = abortSignal
                        }

                        // ask browser for credentials
                        navigator.credentials.get(credentialArguments).then(function (assertion) {
                            // send the assertion to the server for verification
                            if (assertion) {

                                // if we are in a conditional UI flow, the user has just chosen their passkey and authenticated
                                // so we want to disable buttons for the remainder of time (unless an error occurs)
                                if (conditional) {
                                    passwordlessStartCallback();
                                }

                                verifyAssertion(assertion)
                                    .then(function (result) {
                                        if (result.status === "ok") {
                                            // Success!
                                            window.location.href = returnUrl;
                                        }
                                        else {
                                            failLogin(true);
                                        }
                                    },
                                    function () {
                                        failLogin(true);
                                    });
                            }
                            else {
                                if (!conditional) {
                                    passwordlessCompleteCallback();
                                }
                            }
                        }, function (failure) {
                            if (!failure.name.includes('AbortError') && !failure.name.includes('NotAllowedError')) {
                                errorCallback("Passkey login failed or was cancelled.");
                            }

                            if (!conditional) {
                                passwordlessCompleteCallback();
                            }
                        });
                    }
                    else {
                        failLogin(!conditional);
                    }
                })
            }

            // Fire the error callback and optionally the passwordlessCompleteCallback
            function failLogin(triggerCompleteCallback) {

                errorCallback("Passkey login failed or was cancelled.");

                if (triggerCompleteCallback) {
                    passwordlessCompleteCallback();
                }
            }

            function verifyAssertion(assertedCredential) {
                let authData = new Uint8Array(assertedCredential.response.authenticatorData);
                let clientDataJSON = new Uint8Array(assertedCredential.response.clientDataJSON);
                let rawId = new Uint8Array(assertedCredential.rawId);
                let sig = new Uint8Array(assertedCredential.response.signature);
                let userHandle = new Uint8Array(assertedCredential.response.userHandle);

                const data = {
                    id: assertedCredential.id,
                    rawId: coerceToBase64Url(rawId),
                    type: assertedCredential.type,
                    extensions: assertedCredential.getClientExtensionResults(),
                    response: {
                        authenticatorData: coerceToBase64Url(authData),
                        clientDataJson: coerceToBase64Url(clientDataJSON),
                        userHandle: userHandle !== null && userHandle.length > 0 ? coerceToBase64Url(userHandle) : null,
                        signature: coerceToBase64Url(sig)
                    }
                };

                return $.ajax(
                    {
                        type: "POST",
                        url: backendUrls.makeAssertion,
                        data: JSON.stringify(data),
                        contentType: "application/json"
                    });
            }
        }

        //
        // Initialize passkey registration
        //
        function initializeRegistration(jsSelectors, backendUrls, errorCallback, successCallback, options) {

            options = $.extend({}, options);

            $.js(jsSelectors.registerButton).on('click', handleRegisterClick);

            function handleRegisterClick(event) {
                event.preventDefault();

                // possible values: none, direct, indirect.  We use direct so we can get the aaguid for the authenticator.
                let attestation_type = "direct";

                // possible values: <empty>, platform, cross-platform
                let authenticator_attachment = "";

                // possible values: preferred, required, discouraged
                let user_verification = "required";

                // Make credential options server-side
                $.ajax({
                    type: "POST",
                    url: backendUrls.makeCredentialOptions,
                    data: {
                        "attType": attestation_type,
                        "authType": authenticator_attachment,
                        "userVerification": user_verification
                    }
                }).done(function (makeCredentialOptions) {
                    if (makeCredentialOptions.status === "ok") {

                        // Turn the challenge back into the accepted format of padded base64
                        makeCredentialOptions.challenge = coerceToArrayBuffer(makeCredentialOptions.challenge)

                        // Turn ID back into the accepted format of padded base64
                        makeCredentialOptions.user.id = coerceToArrayBuffer(makeCredentialOptions.user.id);

                        makeCredentialOptions.excludeCredentials = makeCredentialOptions.excludeCredentials.map((c) => {
                            c.id = coerceToArrayBuffer(c.id);
                            return c;
                        });

                        if (makeCredentialOptions.authenticatorSelection.authenticatorAttachment === null) {
                            makeCredentialOptions.authenticatorSelection.authenticatorAttachment = undefined;
                        }

                        navigator.credentials.create({
                            publicKey: makeCredentialOptions
                        }).then(function (newCredential) {

                            // register the new credential
                            registerNewCredential(newCredential)
                                .then(function (_, __, xhr) {

                                    // Display a success message to the user
                                    if (options.noRedirect) {
                                        eventBanner.addInformationalMessages("A new passkey has been added to your account.");
                                    }
                                    else {
                                        eventBanner.addDelayedInformationalMessages("A new passkey has been added to your account.");
                                    }

                                    if (successCallback) {
                                        successCallback();
                                    }

                                    if (options.noRedirect) {
                                        return;
                                    }

                                    // Ajax redirect
                                    if (xhr.status == 202) {
                                        window.location = xhr.getResponseHeader("location");
                                    }
                                    else {
                                        // Reload the current url.  We do not use window.location.reload() becuase that will cause
                                        // a POST if the current page was loaded via a POST.  We want a GET, so will just re-set the 
                                        // window location
                                        window.location = window.location.href;
                                    }
                                });
                        }).catch((error) => {
                            errorCallback("Passkey creation failed or was cancelled.");
                        })
                    }
                    else {
                        errorCallback("Passkey creation failed or was cancelled.");
                    }
                });
            }

            // This should be used to verify the auth data with the server
            function registerNewCredential(newCredential) {
                // Move data into Arrays incase it is super long
                let attestationObject = new Uint8Array(newCredential.response.attestationObject);
                let clientDataJSON = new Uint8Array(newCredential.response.clientDataJSON);
                let rawId = new Uint8Array(newCredential.rawId);

                const data = {
                    id: newCredential.id,
                    rawId: coerceToBase64Url(rawId),
                    type: newCredential.type,
                    extensions: newCredential.getClientExtensionResults(),
                    response: {
                        AttestationObject: coerceToBase64Url(attestationObject),
                        clientDataJSON: coerceToBase64Url(clientDataJSON),
                        transports: newCredential.response.getTransports()
                    }
                };

                return $.ajax(
                    {
                        type: "POST",
                        url: backendUrls.makeCredential,
                        data: JSON.stringify(data),
                        contentType: "application/json"
                    });
            }


            function coerceToArrayBuffer(thing, name) {
                if (typeof thing === "string") {
                    // base64url to base64
                    thing = thing.replace(/-/g, "+").replace(/_/g, "/");

                    // base64 to Uint8Array
                    var str = window.atob(thing);
                    var bytes = new Uint8Array(str.length);
                    for (var i = 0; i < str.length; i++) {
                        bytes[i] = str.charCodeAt(i);
                    }
                    thing = bytes;
                }

                // Array to Uint8Array
                if (Array.isArray(thing)) {
                    thing = new Uint8Array(thing);
                }

                // Uint8Array to ArrayBuffer
                if (thing instanceof Uint8Array) {
                    thing = thing.buffer;
                }

                // error if none of the above worked
                if (!(thing instanceof ArrayBuffer)) {
                    throw new TypeError("could not coerce '" + name + "' to ArrayBuffer");
                }

                return thing;
            };
        }

        function coerceToBase64Url(thing) {
            // Array or ArrayBuffer to Uint8Array
            if (Array.isArray(thing)) {
                thing = Uint8Array.from(thing);
            }

            if (thing instanceof ArrayBuffer) {
                thing = new Uint8Array(thing);
            }

            // Uint8Array to base64
            if (thing instanceof Uint8Array) {
                var str = "";
                var len = thing.byteLength;

                for (var i = 0; i < len; i++) {
                    str += String.fromCharCode(thing[i]);
                }
                thing = window.btoa(str);
            }

            if (typeof thing !== "string") {
                throw new Error("could not coerce to string");
            }

            // base64 to base64url
            // NOTE: "=" at the end of challenge is optional, strip it off here
            thing = thing.replace(/\+/g, "-").replace(/\//g, "_").replace(/=*$/g, "");

            return thing;
        };

        // Exported public API
        var exports = {
            initializeRegistration: initializeRegistration,
            initializeLogin: initializeLogin
        };

        return exports;
    }
);